// ─────────────────────────────────────────────────────────────────────────────
// Inventory report formatters — pure display helpers for the read-only
// Accounting V2 inventory screens (stock card, valuation, alerts, ageing).
//
// Why these live here instead of inline in the tabs: the two surfaces these
// screens read disagree about units. `/api/accounting/inventory/*` reports
// every amount in MINOR units (paise) because the ledger engine stores them
// that way, while the stock card returns rupee decimals straight from the
// movement document. Rendering one with the other's formatter would silently
// scale every figure by 100, so the conversion is named at the call site and
// covered by a unit test.
//
// A missing value renders as an em dash, never as 0. Zero is a number somebody
// could act on; an absent field is not.
// ─────────────────────────────────────────────────────────────────────────────

const DASH = '—';

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** An amount stored in paise → `₹1,234.56`. Null / NaN → `—`. */
export function formatMinorAmount(minor: number | null | undefined): string {
  if (!isFiniteNumber(minor)) return DASH;
  return `₹${(minor / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** An amount already in rupees → `₹1,234.57`. Null / NaN → `—`. */
export function formatRupees(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) return DASH;
  return `₹${value.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** A whole quantity with Indian grouping → `1,234`. Null / NaN → `—`. */
export function formatCount(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) return DASH;
  return value.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

/** A real date field → `14/9/2026`. Missing or unparseable → `—`. */
export function formatReportDate(value: string | number | Date | null | undefined): string {
  if (value === null || value === undefined || value === '') return DASH;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return DASH;
  return date.toLocaleDateString('en-IN');
}

/** An elapsed-days figure → `90 days`. Null / NaN → `—` (never guessed). */
export function formatDays(value: number | null | undefined): string {
  if (!isFiniteNumber(value)) return DASH;
  return `${value.toLocaleString('en-IN')} day${value === 1 ? '' : 's'}`;
}
